import type {
  CursorPaginatedResponse,
  Invoice,
  InvoiceListParams,
  InvoicePayload,
} from '@/libs/interfaces'

import { mockInvoices } from '../fixtures/billing'
import { delay, genID, matchesSearch, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const invoiceStore = createEntityStore<Invoice, 'billID'>(mockInvoices, 'billID')

const filterInvoices = (params: InvoiceListParams): Invoice[] =>
  invoiceStore
    .find((invoice) => invoice.storeID === params.storeID)
    .filter((invoice) =>
      matchesSearch(invoice, params.search, ['driverFirstName', 'driverLastName', 'billComments']),
    )

export const invoicesApiMock = {
  create: async (
    params: { orgID: string; storeID: string },
    payload: InvoicePayload,
  ): Promise<Invoice> => {
    const invoice: Invoice = {
      billID: genID('bill'),
      orgID: params.orgID,
      storeID: params.storeID,
      ...payload,
      createdAt: nowISO(),
      updatedAt: nowISO(),
    }
    invoiceStore.insert(invoice)
    return delay(invoice)
  },

  getByID: async (params: { orgID: string; storeID: string; billID: string }): Promise<Invoice> =>
    delay(invoiceStore.getById(params.billID) ?? {}),

  list: async (params: InvoiceListParams): Promise<Invoice[]> => delay(filterInvoices(params)),

  listCursor: async (params: InvoiceListParams): Promise<CursorPaginatedResponse<Invoice>> =>
    delay(paginate(filterInvoices(params), params)),

  update: async (
    params: { orgID: string; storeID: string; billID: string },
    payload: Partial<InvoicePayload>,
  ): Promise<Invoice> => {
    const updated = invoiceStore.update(params.billID, { ...payload, updatedAt: nowISO() })
    return delay(updated ?? {})
  },

  delete: async (params: { orgID: string; storeID: string; billID: string }): Promise<Invoice> => {
    const invoice = invoiceStore.getById(params.billID)
    invoiceStore.remove(params.billID)
    return delay(invoice ?? {})
  },
}
